import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Column } from 'primereact/column'
import { TABLE_CONST } from 'Utils/Constants'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import { DataTable } from 'primereact/datatable'
import { Avatar } from 'primereact/avatar'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import Loader from 'Components/Loader'
import LayoutContainer from 'Components/LayoutComponent'
import DialogUIModal from 'Components/UIComponents/DialogUIModal'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import { getLocalJwtToken, getLocalUserDetails } from 'Utils/LocalStorageHandler'

const ChannelApproval = ({ setLoading }) => {
  const { t } = useTranslation()

  let rowCount = 8
  let paginationButtons = TABLE_CONST.paginationButtonCount

  const [windowSize, setWindowSize] = useState(window.innerWidth)
  const [searchedValue, setSearchedValue] = useState('')
  const [orgList, setOrgList] = useState([])
  const [settingsModalOpen, setSettingsModalOpen] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {}, onSuccess: () => {} })
  const [page, setPage] = useState(0)
  const [rows, setRows] = useState(7)
  const [totalRecords, setTotalRecords] = useState(0)
  const [rejectReason, setRejectReason] = useState('')
  const [isRejectMode, setIsRejectMode] = useState(false)

  const token = getLocalJwtToken()

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  })

  useEffect(() => {
    fetchUnapprovedChannel(page, rows)
  }, [page, rows])

  const fetchUnapprovedChannel = async (pageNo, pageSize) => {
    setShowDialog({
      ...showDialog,
      showModal: false
    })

    try {
      setLoading(true)
      const response = await apiAdapterCoffeeWeb.listOfChannelRequest(pageNo, pageSize, token)
      const data = response?.data

      setOrgList(data?.content)
      setTotalRecords(data?.totalElements ?? 0)
    } catch {
      setShowDialog({
        showModal: true,
        message: t('FAILED_TO_FETCH_THE_DATA'),
        onHide: handleDismiss,
        modalType: 'error'
      })
    } finally {
      setLoading(false)
    }
  }

  const approveChannelRequest = async (requestId) => {
    try {
      setLoading(true)

      await apiAdapterCoffeeWeb.approveChannelRequest(requestId, token)

      setSettingsModalOpen(false)
      setSelectedOrg(null)

      fetchUnapprovedChannel(page, rows)
    } catch (err) {
      console.error(err)

      setShowDialog({
        showModal: true,
        modalType: 'error',
        message: t('FAILED_TO_APPROVE_CHANNEL_REQUEST'),
        onRetry: () => approveChannelRequest(requestId),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const rejectChannelRequest = async (requestId) => {
    try {
      setLoading(true)
      const payload = {
        reason: rejectReason
      }

      await apiAdapterCoffeeWeb.rejectChannelApproval(requestId, token, payload)

      setSettingsModalOpen(false)
      setSelectedOrg(null)
      setRejectReason('')
      setIsRejectMode(false)

      fetchUnapprovedChannel(page, rows)
    } catch {
      setShowDialog({
        showModal: true,
        modalType: 'error',
        message: t('FAILED_TO_REJECT_CHANNEL_REQUEST'),
        onRetry: () => rejectChannelRequest(requestId),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  if (windowSize < TABLE_CONST.maxWidthTab) {
    rowCount = TABLE_CONST.mobileRowCount
    paginationButtons = TABLE_CONST.mobilePaginationButtonCount
  }

  const onPageChange = (event) => {
    setPage(event.page)
    setRows(event.rows)
  }

  const handleWindowResize = () => {
    setWindowSize(window.innerWidth)
  }

  const handleSearchInputChange = (value) => {
    setSearchedValue(value)
  }

  const handleRowClick = (rowData) => {
    setSelectedOrg(rowData)
    setSettingsModalOpen(true)
  }

  const handleCloseDialog = () => {
    setSettingsModalOpen(false)
    setSelectedOrg(null)
    setRejectReason('')
    setIsRejectMode(false)
  }

  const handleDismiss = () => {
    setShowDialog({
      ...showDialog,
      showModal: false
    })
  }

  const organizationFormFooter = () => (
    <div className="organization-form-footer">
      {!isRejectMode ? (
        <>
          <ButtonComponent type={'Custom'} label={'REJECT'} variant="neutral" icon="pi pi-times-circle" onClick={() => setIsRejectMode(true)} />

          <ButtonComponent type={'Custom'} label={'APPROVE'} variant="action" icon="pi pi-check-circle" onClick={() => approveChannelRequest(selectedOrg?.requestId)} />
        </>
      ) : (
        <>
          <ButtonComponent
            type="Custom"
            label="CANCEL"
            variant="neutral"
            onClick={() => {
              setIsRejectMode(false)
              setRejectReason('')
            }}
          />

          <ButtonComponent type={'Custom'} label={'CONFIRM REJECT'} variant="action" icon="pi pi-times-circle" disabled={!rejectReason.trim()} onClick={() => rejectChannelRequest(selectedOrg?.requestId)} />
        </>
      )}
    </div>
  )

  const renderOrgData = () => {
    if (!selectedOrg) return null

    return (
      <div className="org-approval-card">
        <div className="org-image-wrapper">
          <img src={selectedOrg.channelProfileImageUrl} alt={selectedOrg.channelProfileImageUrl} className="org-square-image" />
        </div>

        <div className="org-details-section">
          <div className="org-details-section">
            <label className="org-label">{t('CHANNEL_NAME')}</label>
            <InputText value={selectedOrg.channelName} disabled className="org-disabled-input" />
          </div>

          {isRejectMode && (
            <div className="org-details-section">
              <label className="org-label">{t('REJECT_REASON')}</label>

              <InputText value={rejectReason} placeholder={t('ENTER_REJECT_REASON')} onChange={(e) => setRejectReason(e.target.value)} />
            </div>
          )}
        </div>
      </div>
    )
  }

  const profileImageTemplate = (rowData) => (
    <div className="org-profile-cell">
      <Avatar image={rowData?.channelProfileImageUrl} shape="circle" size="large" alt={rowData?.organisationName} />
    </div>
  )

  const actionTemplate = (rowData) => (
    <div className="table-action-container">
      <Button type="button" icon="pi pi-cog" className="users-setting-icon" tooltip={t('APPROVE')} tooltipOptions={{ position: 'top' }} onClick={() => handleRowClick(rowData)} />
    </div>
  )

  const internalCommunities = [
    {
      dataField: 'channelName',
      text: t('CHANNEL_NAME'),
      className: 'table-org-name',
      sortable: false
    },

    {
      text: t('PROFILE'),
      className: 'table-profile',
      body: profileImageTemplate
    },

    {
      text: t('ACTIONS'),
      className: 'table-actions',
      body: actionTemplate
    }
  ]

  const filteredOrganizations = orgList?.filter((item) => item?.channelName?.toLowerCase()?.includes(searchedValue?.toLowerCase()))

  return (
    <LayoutContainer title={t('CHANNEL_APPROVAL')} breadCrumbsTitle={'channelApproval'} showAddButton={false}>
      <div className="organization-section">
        <div className="org-search p-input-icon-left">
          <i className="org-search-icon pi pi-search" />
          <InputText value={searchedValue} placeholder={t('SEARCH')} className="org-search-input" onChange={(e) => handleSearchInputChange(e.target.value)} />
        </div>

        <div className="organization-table">
          <DataTable value={filteredOrganizations} paginator lazy first={page * rows} rows={rows} totalRecords={totalRecords} pageLinkSize={paginationButtons} onPage={onPageChange}>
            {internalCommunities?.map((column, index) => (
              <Column key={index} field={column.dataField} header={column.text} body={column.body} className={column.className} sortable={column.sortable} />
            ))}
          </DataTable>
        </div>

        <DialogModal {...showDialog} onDismiss={handleDismiss} />
      </div>

      <DialogUIModal visible={settingsModalOpen} header={t('CHANNEL_DETAILES')} onHide={handleCloseDialog} body={renderOrgData} footer={organizationFormFooter} className="organization-modal" size="md" maximizable={false} />
    </LayoutContainer>
  )
}

export default Loader(ChannelApproval)
